/**
 * System → Benachrichtigungsversand (DEV-126).
 *
 * The available mail transports and the active one, each transport's own
 * settings with „Verbindung testen“ where it offers one, the global rules of
 * the dispatch job and its figures. „Manuell“ is the default: then no job runs
 * and queued mails wait in the Postausgang. A transport bound to a condition
 * (e.g. the demo variant) is not offered where it is not met.
 */
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import Spinner from '../../components/Spinner';
import {
  errorMessage, fetchMailDispatchSettings, fetchMailDispatchStatus, testMailTransport, updateMailDispatchSettings,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { DispatchSetting, MailDispatchSettings, MailDispatchStatus, MailTransportInfo } from '../../lib/types';

import { changedValues, parseSetting, rawOf, type RawValue } from './dispatchModel';
import './mail-dispatch.css';

type Raws = Record<string, RawValue>;

function SettingField({ setting, idBase, raw, onChange }: Readonly<{
  setting: DispatchSetting;
  idBase: string;
  raw: RawValue;
  onChange: (raw: RawValue) => void;
}>): ReactNode {
  // Validation messages and hints are in the UI language; labels and help come from the server.
  const { t } = useLingui();
  const id = `${idBase}-${setting.key}`;
  if (setting.type === 'boolean') {
    return (
      <label className="check">
        <input id={id} type="checkbox" checked={Boolean(raw)} onChange={(e) => onChange(e.target.checked)} />
        {setting.label}
      </label>
    );
  }
  const { error } = parseSetting(setting, raw);
  const label = `${setting.label}${setting.required ? ' *' : ''}`;
  if (setting.type === 'choice') {
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <select id={id} value={String(raw)} onChange={(e) => onChange(e.target.value)}>
          {setting.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {setting.help ? <span className="hint">{setting.help}</span> : null}
      </div>
    );
  }
  const numeric = setting.type === 'integer' || setting.type === 'number';
  let hint = setting.help;
  if (setting.type === 'secret') hint = setting.configured ? t`Gespeichert — leer lassen behält den Wert.` : t`Noch nicht gesetzt.`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={setting.type === 'secret' ? 'password' : 'text'}
        inputMode={numeric ? 'decimal' : undefined}
        autoComplete={setting.type === 'secret' ? 'new-password' : 'off'}
        className={numeric ? 'dispatch-number' : undefined}
        value={String(raw)}
        aria-invalid={error !== null}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <span className="err">{error}</span> : null}
      {!error && hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

/** The last run of the dispatch job in one line. */
function LastRun({ run }: Readonly<{ run: NonNullable<MailDispatchStatus['last_run']> | null }>): ReactNode {
  const { t } = useLingui();
  if (!run) return t`Noch kein Lauf seit dem Start`;
  const at = fmtDate(run.at);
  const { error: runError, sent, retry, failed, interrupted } = run;
  if (runError) return t`${at} · Fehler: ${runError}`;
  if (interrupted) {
    return t`${at} · ${sent} versendet · ${retry} wird wiederholt · ${failed} fehlgeschlagen · ${interrupted} unterbrochen`;
  }
  return t`${at} · ${sent} versendet · ${retry} wird wiederholt · ${failed} fehlgeschlagen`;
}

function DispatchStatusCard({ status, error }: Readonly<{ status: MailDispatchStatus | null; error: string | null }>): ReactNode {
  const { t } = useLingui();
  const run = status?.last_run ?? null;
  const waiting = status?.waiting_for_decision ?? 0;
  return (
    <div className="card">
      <div className="card-head"><h2><Trans>Stand</Trans></h2></div>
      <div className="card-body">
        {error ? <Banner kind="error"><Trans>Stand nicht verfügbar: {error}</Trans></Banner> : null}
        {status === null && error === null ? <Spinner label={t`Wird geladen…`} /> : null}
        {status ? (
          <dl className="detail-list dispatch-status">
            <dt><Trans>Eingereiht</Trans></dt>
            <dd>{status.queued}</dd>
            <dt><Trans>Fehlgeschlagen</Trans></dt>
            <dd>
              {status.failed > 0 ? <Chip tone="alert">{status.failed}</Chip> : '0'}
              {status.failed > 0 ? <> · <Link to="/benachrichtigungen"><Trans>im Postausgang erneut einreihen</Trans></Link></> : null}
            </dd>
            {status.waiting_for_decision > 0 ? (
              <>
                <dt><Trans>Warten auf Entscheidung</Trans></dt>
                <dd>
                  <Trans>{waiting} — keine Version mehr betroffen; „Trotzdem senden“ oder zurückziehen</Trans>
                </dd>
              </>
            ) : null}
            <dt><Trans>Letzter Lauf</Trans></dt>
            <dd><LastRun run={run} /></dd>
            <dt><Trans>Nächster Lauf</Trans></dt>
            <dd>{status.next_run ? fmtDate(status.next_run) : t`Kein Versand-Job — „Manuell“ ist aktiv`}</dd>
          </dl>
        ) : null}
      </div>
    </div>
  );
}

export function SystemMailDispatchPage(): ReactNode {
  const { t } = useLingui();
  const [settings, setSettings] = useState<MailDispatchSettings | null>(null);
  const [status, setStatus] = useState<MailDispatchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState('manual');
  const [globalRaws, setGlobalRaws] = useState<Raws>({});
  const [transportRaws, setTransportRaws] = useState<Record<string, Raws>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const loadStatus = useCallback(() => {
    fetchMailDispatchStatus()
      .then((next) => { setStatus(next); setStatusError(null); })
      .catch((err: unknown) => setStatusError(errorMessage(err)));
  }, []);

  const apply = useCallback((next: MailDispatchSettings) => {
    setSettings(next);
    setActive(next.active_transport);
    setGlobalRaws(Object.fromEntries(next.global.map((s) => [s.key, rawOf(s)])));
    setTransportRaws(Object.fromEntries(next.transports.map((entry) => [
      entry.key, Object.fromEntries(entry.settings.map((s) => [s.key, rawOf(s)])),
    ])));
  }, []);

  useEffect(() => {
    fetchMailDispatchSettings().then(apply).catch((err: unknown) => setError(errorMessage(err)));
    loadStatus();
  }, [apply, loadStatus]);

  const transport: MailTransportInfo | null = settings?.transports.find((entry) => entry.key === active) ?? null;
  const settingsHeading = (label: string): string => t`Einstellungen „${label}“`;
  const missingHint = (fields: readonly string[]): string => {
    const list = fields.join(', ');
    return t`Vor dem Aktivieren ausfüllen: ${list}`;
  };
  const globalChanges = settings ? changedValues(settings.global, globalRaws) : {};
  const transportChanges = transport ? changedValues(transport.settings, transportRaws[transport.key] ?? {}) : {};
  const valid = globalChanges !== null && transportChanges !== null;
  const dirty = settings !== null && (active !== settings.active_transport
    || Object.keys(globalChanges ?? {}).length > 0 || Object.keys(transportChanges ?? {}).length > 0);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!valid || !dirty || saving || transport === null) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      apply(await updateMailDispatchSettings({
        active_transport: active,
        global: globalChanges ?? {},
        transports: Object.keys(transportChanges ?? {}).length > 0 ? { [transport.key]: transportChanges ?? {} } : {},
      }));
      setSaved(t`Gespeichert. Die Änderungen gelten ab dem nächsten Lauf.`);
      loadStatus();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function test(key: string): Promise<void> {
    setTestResult(null);
    try {
      setTestResult(await testMailTransport(key));
    } catch (err) {
      setTestResult({ ok: false, message: errorMessage(err) });
    }
  }

  return (
    <>
      <DispatchStatusCard status={status} error={statusError} />
      <form onSubmit={(event) => void save(event)}>
        <div className="card">
          <div className="card-head"><h2><Trans>Transport</Trans></h2></div>
          <div className="card-body">
            {error ? <Banner kind="error">{error}</Banner> : null}
            {saved ? <Banner kind="ok">{saved}</Banner> : null}
            {settings === null && error === null ? <Spinner label={t`Wird geladen…`} /> : null}
            {settings ? (
              <fieldset className="dispatch-transports">
                <legend className="visually-hidden"><Trans>Aktiver Transport</Trans></legend>
                {settings.transports.map((entry) => (
                  <label key={entry.key} className={`dispatch-transport${entry.key === active ? ' on' : ''}`}>
                    <input type="radio" name="transport" value={entry.key} checked={entry.key === active}
                      onChange={() => { setActive(entry.key); setTestResult(null); setSaved(null); }} />
                    <span>
                      <strong>{entry.label}</strong>
                      {entry.active ? <Chip tone="good"><Trans>aktiv</Trans></Chip> : null}
                      {entry.simulated ? <Chip><Trans>simuliert</Trans></Chip> : null}
                      <span className="hint">{entry.description}</span>
                      {entry.missing.length > 0 ? (
                        <span className="hint">{missingHint(entry.missing)}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </fieldset>
            ) : null}
            {transport && transport.settings.length > 0 ? (
              <div className="dispatch-settings">
                <h3>{settingsHeading(transport.label)}</h3>
                <div className="dispatch-fields">
                  {transport.settings.map((setting) => (
                    <SettingField key={setting.key} setting={setting} idBase={`transport-${transport.key}`}
                      raw={transportRaws[transport.key]?.[setting.key] ?? rawOf(setting)}
                      onChange={(raw) => {
                        setSaved(null);
                        setTransportRaws((current) => ({
                          ...current, [transport.key]: { ...current[transport.key], [setting.key]: raw },
                        }));
                      }} />
                  ))}
                </div>
              </div>
            ) : null}
            {transport?.can_test ? (
              <div className="dispatch-test">
                <button type="button" className="ghost" onClick={() => void test(transport.key)}><Trans>Verbindung testen</Trans></button>
                <span className="hint"><Trans>mit den gespeicherten Einstellungen</Trans></span>
                {testResult ? <Banner kind={testResult.ok ? 'ok' : 'error'}>{testResult.message}</Banner> : null}
              </div>
            ) : null}
          </div>
        </div>

        {settings ? (
          <div className="card">
            <div className="card-head"><h2><Trans>Versandregeln</Trans></h2></div>
            <div className="card-body">
              <p className="hint">
                <Trans>
                  Gelten für automatische Transporte: Der Versand-Job arbeitet die eingereihten Nachrichten im
                  Intervall ab. Ein vorübergehender Fehler wird nach wachsender Wartezeit wiederholt; nach dem letzten
                  Versuch steht die Nachricht auf „Fehlgeschlagen“.
                </Trans>
              </p>
              <div className="dispatch-fields">
                {settings.global.map((setting) => (
                  <SettingField key={setting.key} setting={setting} idBase="dispatch"
                    raw={globalRaws[setting.key] ?? rawOf(setting)}
                    onChange={(raw) => { setSaved(null); setGlobalRaws((current) => ({ ...current, [setting.key]: raw })); }} />
                ))}
              </div>
              <div className="ops-dt-actions">
                <span />
                <button type="submit" className="primary" disabled={!valid || !dirty || saving}>
                  {saving ? <Spinner variant="on-accent" label={t`Wird gespeichert…`} /> : t`Speichern`}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </>
  );
}

export default SystemMailDispatchPage;
